import React from "react";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";

const PrivacyPolicy = () => {
  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h2" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
          <b>Updated:</b> July 18, 2023
        </Typography>
        <Typography variant="body1" paragraph>
          We, Malayalam.ai ("Malayalam.ai", "we", "our" or "us"), value your
          privacy and are fully committed to ensuring the security of any
          information we obtain from or about you. This Privacy Policy outlines
          our practices regarding the collection and usage of Personal
          Information when you interact with our website, applications, and
          services (collectively referred to as the "Services").
        </Typography>
        <Typography variant="h4" gutterBottom>
          Personal information we collect
        </Typography>
        <Typography variant="body1" paragraph component="div">
          At Malayalam.ai, we are committed to safeguarding your privacy and
          protecting your personal information ("Personal Information"). We
          collect Personal Information in the following ways:
          <br />
          <br />
          <strong>Personal Information You Provide:</strong> When you create an
          account or communicate with us, we collect Account Information, User
          Content, Communication Information, and Social Media Information. This
          includes details such as your name, contact information, account
          credentials, payment card information, transaction history, and any
          information you provide in your communications with us or through
          social media platforms.
          <ul>
            <li>
              Account Information: When you create an account with us, we will
              collect information associated with your account, including your
              name, contact information, account credentials, payment card
              information, and transaction history, (collectively, “Account
              Information”).
            </li>
            <li>
              User Content: When you use our Services, we collect Personal
              Information that is included in the input, file uploads, or
              feedback that you provide to our Services (“Content”).{" "}
            </li>
            <li>
              Communication Information: If you communicate with us, we collect
              your name, contact information, and the contents of any messages
              you send (“Communication Information”).
            </li>
            <li>
              Social Media Information: We have pages on social media sites like
              Instagram, Facebook, Medium, Twitter, YouTube and LinkedIn. When
              you interact with our social media pages, we will collect Personal
              Information that you elect to provide to us, such as your contact
              details (collectively, “Social Information”). In addition, the
              companies that host our social media pages may provide us with
              aggregate information and analytics about our social media
              activity.
            </li>
          </ul>
          <strong>Personal Information We Receive Automatically:</strong> When
          you use our Services, we automatically receive Technical Information.
          This includes Log Data, Usage Data, Device Information, Cookies, and
          Analytics. Log Data includes information sent by your browser, such as
          your IP address, browser type and settings, date and time of your
          request, and your interactions with our website. Usage Data includes
          information about the content you view or engage with, features you
          use, your time zone, country, access dates and times, user agent and
          version, device type and settings, and your computer connection.
          Device Information includes details about the device you use, such as
          its name, operating system, device identifiers, and browser. We also
          use cookies, which are small pieces of data sent to your browser, to
          improve your experience and administer our Services. Finally, we may
          utilize analytics products that use cookies to analyze user behavior
          and enhance your overall experience.
          <ul>
            <li>
              Log Data: Information that your browser automatically sends when
              you use our Services. Log data includes your Internet Protocol
              address, browser type and settings, the date and time of your
              request, and how you interact with our website.
            </li>
            <li>
              Usage Data: We may automatically collect information about your
              use of the Services, such as the types of content that you view or
              engage with, the features you use and the actions you take, as
              well as your time zone, country, the dates and times of access,
              user agent and version, type of computer or mobile device, and
              your computer connection.
            </li>
            <li>
              Device Information: Includes name of the device, operating system,
              device identifiers, and browser you are using. Information
              collected may depend on the type of device you use and its
              settings.
            </li>
            <li>
              Cookies: We use cookies to operate and administer our Services,
              and improve your experience. A “cookie” is a piece of information
              sent to your browser by a website you visit. You can set your
              browser to accept all cookies, to reject all cookies, or to notify
              you whenever a cookie is offered so that you can decide each time
              whether to accept it. However, refusing a cookie may in some cases
              preclude you from using, or negatively affect the display or
              function of, a website or certain areas or features of a website.
              For more details on cookies, please visit All About Cookies.
            </li>
            <li>
              Analytics: We may use a variety of online analytics products that
              use cookies to help us analyze how users use our Services and
              enhance your experience when you use the Services.
            </li>
          </ul>
          <br />
          Please be assured that we handle your Personal Information with the
          utmost care, in accordance with applicable privacy laws and
          regulations. We use the collected information to provide and improve
          our Services, personalize your experience, ensure the security of our
          platform, and comply with legal obligations. For more detailed
          information on how we handle and protect your Personal Information,
          please refer to our Privacy Policy.
        </Typography>
        <Typography variant="h4" gutterBottom>
          How we use personal information
        </Typography>
        <Typography variant="body1" paragraph component="div">
          We may use Personal Information for the following purposes:
          <ul>
            <li>
              To provide, administer, maintain, and/or analyze the Services;
            </li>
            <li>To improve our Services and conduct research;</li>
            <li>To communicate with you;</li>
            <li>To develop new programs and services;</li>
            <li>
              To prevent fraud, criminal activity, or misuses of our Services,
              and to protect the security of our IT systems, architecture, and
              networks;
            </li>
            <li>To carry out business transfers;</li>
            <li>
              To comply with legal obligations and legal process and to protect
              our rights, privacy, safety, or property, and/or that of our
              affiliates, you, or other third parties.
            </li>
          </ul>
          <strong>Aggregated or De-Identified Information:</strong>
          <br />
          We may aggregate or de-identify Personal Information so that it may no
          longer be used to identify you and use such information to analyze the
          effectiveness of our Services, to improve and add features to our
          Services, to conduct research and for other similar purposes. In
          addition, from time to time, we may analyze the general behavior and
          characteristics of users of our Services and share aggregated
          information like general user statistics with third parties, publish
          such aggregated information or make such aggregated information
          generally available. We may collect aggregated information through the
          Services, through cookies, and through other means described in this
          Privacy Policy. We will maintain and use de-identified information in
          anonymous or de-identified form and we will not attempt to reidentify
          the information, unless required by law.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Disclosure of personal information
        </Typography>
        <Typography variant="body1" paragraph component="div">
          In certain circumstances we may provide your Personal Information to
          third parties without further notice to you, unless required by the
          law:
        </Typography>
        <Typography variant="h4" gutterBottom>
          Your rights
        </Typography>
        <Typography variant="body1" paragraph component="div">
          Depending on location, individuals in the EEA, the UK, and across the
          globe may have certain statutory rights in relation to their Personal
          Information. For example, you may have the right to:
          <ul>
            <li>
              Access your Personal Information and information relating to how
              it is processed.
            </li>
            <li>Delete your Personal Information from our records.</li>
            <li>Rectify or update your Personal Information.</li>
            <li>
              Transfer your Personal Information to a third party (right to data
              portability).
            </li>
            <li>Restrict how we process your Personal Information.</li>
            <li>
              Withdraw your consent—where we rely on consent as the legal basis
              for processing at any time.
            </li>
            <li>Object to how we process your Personal Information.</li>
            <li>
              Lodge a complaint with your local data protection authority.
            </li>
          </ul>
          You can exercise some of these rights through your Malayalam.ai
          account. If you are unable to exercise your rights through your
          account, please send your request to dsar@malayalam.ai. Services like
          ChatGPT generate responses by reading a user’s request and, in
          response, predicting the words most likely to appear next. In some
          cases, the words most likely to appear next may not be the most
          factually accurate. For this reason, you should not rely on the
          factual accuracy of output from our models. If you notice that ChatGPT
          output contains factually inaccurate information about you and you
          would like us to correct the inaccuracy, you may submit a correction
          request to dsar@malayalam.ai. Given the technical complexity of how
          our models work, we may not be able to correct the inaccuracy in every
          instance. In that case, you may request that we remove your Personal
          Information from ChatGPT’s output by filling out this form.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Children
        </Typography>
        <Typography variant="body1" paragraph component="div">
          Our Service is not directed to children under the age of 13. OpenAI
          does not knowingly collect Personal Information from children under
          the age of 13. If you have reason to believe that a child under the
          age of 13 has provided Personal Information to OpenAI through the
          Service, please email us at legal@openai.com. We will investigate any
          notification and if appropriate, delete the Personal Information from
          our systems. If you are 13 or older, but under 18, you must have
          consent from your parent or guardian to use our Services.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Security and Retention
        </Typography>
        <Typography variant="body1" paragraph component="div">
          We implement commercially reasonable technical, administrative, and
          organizational measures to protect Personal Information both online
          and offline from loss, misuse, and unauthorized access, disclosure,
          alteration, or destruction. However, no Internet or email transmission
          is ever fully secure or error free. In particular, email sent to or
          from us may not be secure. Therefore, you should take special care in
          deciding what information you send to us via the Service or email. In
          addition, we are not responsible for circumvention of any privacy
          settings or security measures contained on the Service, or third-party
          websites.
          <br /> <br />
          We’ll retain your Personal Information for only as long as we need in
          order to provide our Service to you, or for other legitimate business
          purposes such as resolving disputes, safety and security reasons, or
          complying with our legal obligations. How long we retain Personal
          Information will depend on a number of factors, such as the amount,
          nature, and sensitivity of the information, the potential risk of harm
          from unauthorized use or disclosure, our purpose for processing the
          information, and any legal requirements.
        </Typography>
        <Typography variant="h4" gutterBottom>
          International users
        </Typography>
        <Typography variant="body1" paragraph component="div">
          By using our Service, you understand and acknowledge that your
          Personal Information will be processed and stored in our facilities
          and servers in the United States and may be disclosed to our service
          providers and affiliates in other jurisdictions.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Changes to the privacy policy
        </Typography>
        <Typography variant="body1" paragraph component="div">
          We may update this Privacy Policy from time to time. When we do, we
          will post an updated version on this page, unless another type of
          notice is required by applicable law.
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
